import React, { useEffect, useCallback, useRef, useState } from 'react';
import { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';

interface DocumentEditorProps {
  content: string;
  onContentChange: (content: string) => void;
  onInsertPosition: (position: number) => void;
  loading?: boolean;
  readOnly?: boolean;
}

export const DocumentEditor: React.FC<DocumentEditorProps> = ({
  content,
  onContentChange,
  onInsertPosition,
  loading = false,
  readOnly = false
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [editor, setEditor] = useState<Editor | null>(null);
  const [insertPosition, setInsertPosition] = useState<number>(0);

  // 初始化编辑器
  useEffect(() => {
    if (!editorRef.current || loading) return;

    const editorInstance = new Editor({
      element: editorRef.current,
      extensions: [
        StarterKit.configure({
          heading: {
            levels: [1, 2, 3, 4, 5, 6],
          },
        }),
      ],
      content: content,
      editable: true, // 始终可编辑，这样才能显示光标
      editorProps: {
        attributes: {
          class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none',
          style: 'height: 100%; padding: 12px; outline: none; overflow-y: auto; cursor: text;',
          'data-component': 'document-editor'
        },
        // 在只读模式下拦截所有修改操作
        handleKeyDown: readOnly ? () => true : undefined,
        handleTextInput: readOnly ? () => true : undefined,
        handlePaste: readOnly ? () => true : undefined,
        handleDrop: readOnly ? () => true : undefined,
      },
      onUpdate: ({ editor }) => {
        if (!readOnly) {
          const html = editor.getHTML();
          onContentChange(html);
        } else {
          // 只读模式下阻止内容更改，恢复原内容
          if (content !== editor.getHTML()) {
            editor.commands.setContent(content);
          }
        }
      },
      onSelectionUpdate: ({ editor }) => {
        const { from } = editor.state.selection;
        setInsertPosition(from);
        onInsertPosition(from);
        console.log('[DocumentEditor] 选择位置:', from);
      },
    });

    setEditor(editorInstance);

    // 清理函数
    return () => {
      if (editorInstance) {
        editorInstance.destroy();
      }
    };
  }, [loading, readOnly, content]);

  // 当外部content变化时更新编辑器内容
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  // 插入文本的方法
  const insertTextAtPosition = useCallback((text: string, position: number) => {
    if (editor && !readOnly) {
      const htmlText = `<p>${text.replace(/\n/g, '</p><p>')}</p>`;
      editor.chain()
        .focus()
        .setTextSelection(position)
        .insertContent(htmlText)
        .run();
    }
  }, [editor, readOnly]);

  // 暴露插入方法给父组件
  useEffect(() => {
    if (editor && !readOnly) {
      (window as any).insertTextToEditor = insertTextAtPosition;
    }
  }, [insertTextAtPosition, editor, readOnly]);

  // 工具栏按钮点击处理（只有在非只读模式下才有效）
  const handleBold = useCallback(() => {
    if (!readOnly) {
      editor?.chain().focus().toggleBold().run();
    }
  }, [editor, readOnly]);

  const handleItalic = useCallback(() => {
    if (!readOnly) {
      editor?.chain().focus().toggleItalic().run();
    }
  }, [editor, readOnly]);

  const handleStrike = useCallback(() => {
    if (!readOnly) {
      editor?.chain().focus().toggleStrike().run();
    }
  }, [editor, readOnly]);

  const handleCode = useCallback(() => {
    if (!readOnly) {
      editor?.chain().focus().toggleCode().run();
    }
  }, [editor, readOnly]);

  const handleHeading = useCallback((level: 1 | 2 | 3 | 4 | 5 | 6) => {
    if (!readOnly) {
      editor?.chain().focus().toggleHeading({ level }).run();
    }
  }, [editor, readOnly]);

  const handleBulletList = useCallback(() => {
    if (!readOnly) {
      editor?.chain().focus().toggleBulletList().run();
    }
  }, [editor, readOnly]);

  const handleOrderedList = useCallback(() => {
    if (!readOnly) {
      editor?.chain().focus().toggleOrderedList().run();
    }
  }, [editor, readOnly]);

  const handleBlockquote = useCallback(() => {
    if (!readOnly) {
      editor?.chain().focus().toggleBlockquote().run();
    }
  }, [editor, readOnly]);

  const handleHorizontalRule = useCallback(() => {
    if (!readOnly) {
      editor?.chain().focus().setHorizontalRule().run();
    }
  }, [editor, readOnly]);

  const handleUndo = useCallback(() => {
    if (!readOnly) {
      editor?.chain().focus().undo().run();
    }
  }, [editor, readOnly]);

  const handleRedo = useCallback(() => {
    if (!readOnly) {
      editor?.chain().focus().redo().run();
    }
  }, [editor, readOnly]);

  if (loading) {
    return (
      <div style={{ 
        flex: 1, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        border: '1px solid #ddd',
        borderRadius: '4px',
        minHeight: '200px'
      }}>
        加载文档内容中...
      </div>
    );
  }

  const toolbarStyle: React.CSSProperties = {
    borderBottom: '1px solid #ddd',
    padding: '8px',
    marginBottom: '8px',
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    flexShrink: 0,
    opacity: readOnly ? 0.5 : 1
  };

  const buttonStyle: React.CSSProperties = {
    padding: '4px 8px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    backgroundColor: 'white',
    cursor: readOnly ? 'not-allowed' : 'pointer',
    fontSize: '12px'
  };

  const activeButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: '#007bff',
    color: 'white'
  };

  return (
    <div style={{ 
      flex: 1, 
      display: 'flex', 
      flexDirection: 'column',
      minHeight: 0
    }}>
      <div style={{ 
        marginBottom: '8px', 
        fontSize: '12px', 
        color: '#666',
        flexShrink: 0
      }}>
        {readOnly 
          ? `文档预览模式 - 点击选择插入位置 (当前位置: ${insertPosition})` 
          : '点击文档内容任意位置选择插入点，使用工具栏格式化文本'
        }
      </div>
      
      {/* 只有在非只读模式下才显示工具栏 */}
      {editor && !readOnly && (
        <div style={toolbarStyle}>
          <button
            onClick={handleBold}
            style={editor.isActive('bold') ? activeButtonStyle : buttonStyle}
            title="粗体"
          >
            <strong>B</strong>
          </button>
          <button
            onClick={handleItalic}
            style={editor.isActive('italic') ? activeButtonStyle : buttonStyle}
            title="斜体"
          >
            <em>I</em>
          </button>
          <button
            onClick={handleStrike}
            style={editor.isActive('strike') ? activeButtonStyle : buttonStyle}
            title="删除线"
          >
            <s>S</s>
          </button>
          <button
            onClick={handleCode}
            style={editor.isActive('code') ? activeButtonStyle : buttonStyle}
            title="代码"
          >
            Code
          </button>
          <button
            onClick={() => handleHeading(1)}
            style={editor.isActive('heading', { level: 1 }) ? activeButtonStyle : buttonStyle}
            title="标题1"
          >
            H1
          </button>
          <button
            onClick={() => handleHeading(2)}
            style={editor.isActive('heading', { level: 2 }) ? activeButtonStyle : buttonStyle}
            title="标题2"
          >
            H2
          </button>
          <button
            onClick={() => handleHeading(3)}
            style={editor.isActive('heading', { level: 3 }) ? activeButtonStyle : buttonStyle}
            title="标题3"
          >
            H3
          </button>
          <button
            onClick={handleBulletList}
            style={editor.isActive('bulletList') ? activeButtonStyle : buttonStyle}
            title="无序列表"
          >
            • List
          </button>
          <button
            onClick={handleOrderedList}
            style={editor.isActive('orderedList') ? activeButtonStyle : buttonStyle}
            title="有序列表"
          >
            1. List
          </button>
          <button
            onClick={handleBlockquote}
            style={editor.isActive('blockquote') ? activeButtonStyle : buttonStyle}
            title="引用"
          >
            Quote
          </button>
          <button
            onClick={handleHorizontalRule}
            style={buttonStyle}
            title="分隔线"
          >
            ―
          </button>
          <button
            onClick={handleUndo}
            disabled={!editor.can().undo()}
            style={{
              ...buttonStyle,
              opacity: editor.can().undo() ? 1 : 0.5,
              cursor: editor.can().undo() && !readOnly ? 'pointer' : 'not-allowed'
            }}
            title="撤销"
          >
            ↶
          </button>
          <button
            onClick={handleRedo}
            disabled={!editor.can().redo()}
            style={{
              ...buttonStyle,
              opacity: editor.can().redo() ? 1 : 0.5,
              cursor: editor.can().redo() && !readOnly ? 'pointer' : 'not-allowed'
            }}
            title="重做"
          >
            ↷
          </button>
        </div>
      )}
      
      <div style={{ 
        flex: 1,
        border: '1px solid #ddd', 
        borderRadius: '4px',
        minHeight: '200px',
        overflow: 'hidden',
        position: 'relative'
      }}>
        <div 
          ref={editorRef}
          style={{ 
            height: '100%',
            width: '100%'
          }}
        />
        {/* 只读模式提示 */}
        {readOnly && (
          <div style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            backgroundColor: 'rgba(0, 123, 255, 0.1)',
            color: '#007bff',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '10px',
            fontWeight: 'bold',
            pointerEvents: 'none'
          }}>
            预览模式
          </div>
        )}
      </div>
    </div>
  );
};